import {
  buildDiscountCodeList,
  getPrimaryDiscountCodeFromUrl,
  isAdditiveFreeProcessingDiscountCode,
} from '@theme/free-processing-discount';

const normalizeCode = (code) =>
  typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;

const clearDiscountUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('lb_discount');
  url.searchParams.delete('lb_discount_type');
  url.searchParams.delete('discount');
  window.history.replaceState({}, '', url);
};

/**
 * Applies discounts before the cart contains items. The metadata worker is required here because
 * Shopify may report an otherwise valid product/order code as not applicable until qualifying
 * lines exist.
 */
export async function bootstrapEmptyCartDiscount() {
  if (document.querySelector('cart-discount-component')) return;

  const config = Theme.legacyBox.discounts;
  const clearDefault = () => sessionStorage.removeItem(config.defaultSessionKey);
  const isOverride = (code) =>
    Boolean(
      code &&
      config.overrides.some(
        (override) => !override.additive && normalizeCode(override.auto_discount) === code
      )
    );
  const readStored = () => {
    const value = sessionStorage.getItem(config.sessionKey);
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch {
      sessionStorage.removeItem(config.sessionKey);
      return null;
    }
  };
  const resolveAutoCode = async (fallbackCode, triggerAssignment = false) => {
    if (!config.autoApply.active || !fallbackCode) return null;

    try {
      return (
        (await window.Legacybox?.resolveSitewideAutoApplyDiscountCode?.({
          fallbackCode,
          triggerAssignment,
        })) || fallbackCode
      );
    } catch (error) {
      console.error(error);
      return fallbackCode;
    }
  };
  const applyDiscount = async (code, appliedType = 'auto_apply') => {
    if (!code) return false;

    let discountData = { code: normalizeCode(code) };
    let hasWorkerData = false;

    try {
      const response = await fetch(config.worker, {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      if (!response.ok) throw new Error(`Discount lookup failed (${response.status})`);

      const payload = await response.json();
      discountData = payload.data || discountData;
      hasWorkerData = discountData.status === 'success';
    } catch (error) {
      console.error(error);
    }

    if (discountData.status === 'fail' && !isOverride(normalizeCode(code))) return false;

    const response = await fetch(Theme.routes.cart_update_url, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ discount: buildDiscountCodeList(code).join(',') }),
    });
    if (!response.ok) throw new Error(`Discount update failed (${response.status})`);

    const data = await response.json();
    if (isAdditiveFreeProcessingDiscountCode(code)) {
      clearDefault();
    } else {
      const sessionData = JSON.stringify({
        applied: appliedType,
        ...discountData,
        ...(window.Legacybox?.buildSitewideDiscountAbSessionData?.(code, appliedType) || {}),
      });

      sessionStorage.setItem(config.sessionKey, sessionData);
      if (appliedType === 'auto_apply' && hasWorkerData) {
        sessionStorage.setItem(config.defaultSessionKey, sessionData);
      } else {
        clearDefault();
      }
    }

    clearDiscountUrl();
    document.dispatchEvent(new CustomEvent('discount:update', { detail: { data } }));
    return true;
  };

  try {
    const params = new URLSearchParams(window.location.search);
    const urlCode = getPrimaryDiscountCodeFromUrl();
    const requestedType = params.get('lb_discount_type') || 'url_query';
    const requestedCode = normalizeCode(urlCode);
    let storedDiscount = readStored();

    if (isAdditiveFreeProcessingDiscountCode(storedDiscount?.code)) {
      sessionStorage.removeItem(config.sessionKey);
      storedDiscount = null;
    }

    if (storedDiscount) {
      const storedCode = normalizeCode(storedDiscount.code);

      if (requestedCode) {
        if (storedCode === requestedCode) {
          if (buildDiscountCodeList(storedCode).length > 1) {
            if (!(await applyDiscount(storedCode, requestedType))) return;
          }
          clearDefault();
          clearDiscountUrl();
          return;
        }
        await applyDiscount(urlCode, requestedType);
        return;
      }

      if (storedDiscount.applied !== 'auto_apply' || isOverride(storedCode)) {
        if (buildDiscountCodeList(storedCode).length > 1) {
          await applyDiscount(storedCode, storedDiscount.applied);
        }
        clearDefault();
        return;
      }

      const autoCode = await resolveAutoCode(storedCode || config.autoApply.code);
      const defaultCode = normalizeCode(autoCode);
      if (storedCode === defaultCode) {
        if (buildDiscountCodeList(storedCode).length > 1) {
          await applyDiscount(storedCode, 'auto_apply');
        } else {
          sessionStorage.setItem(config.defaultSessionKey, JSON.stringify(storedDiscount));
        }
        return;
      }

      if (autoCode) await applyDiscount(autoCode, 'auto_apply');
      return;
    }

    const autoCode = requestedCode ? null : await resolveAutoCode(config.autoApply.code, true);
    const campaignCode = buildDiscountCodeList(null)[0] || null;
    const code = urlCode || autoCode || campaignCode;
    const type = requestedCode ? requestedType : autoCode ? 'auto_apply' : 'campaign';

    if (!(await applyDiscount(code, type)) && campaignCode && code !== campaignCode) {
      await applyDiscount(campaignCode, 'campaign');
    }
  } catch (error) {
    console.error(error);
  } finally {
    document.body.classList.remove('discounts-loading');
  }
}

document.addEventListener('DOMContentLoaded', bootstrapEmptyCartDiscount);
